import * as THREE from "three";
import * as CANNON from "cannon-es";
import { XRHandModelFactory } from "three/addons/webxr/XRHandModelFactory.js";
import "./desktop-ui";
import { ToolPanel } from "./tool-panel";
import { startPartsTool, type PartsToolHandle } from "./parts-tool";

const statusEl = document.getElementById("status") as HTMLDivElement;
const enterBtn = document.getElementById("enter-ar") as HTMLButtonElement;
const overlayEl = document.getElementById("overlay") as HTMLDivElement;
const appEl = document.getElementById("app") as HTMLDivElement;

const setStatus = (msg: string) => { statusEl.textContent = msg; };

// ── Three.js scene ───────────────────────────────────────────────────────
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.01, 50);

const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
renderer.setPixelRatio(window.devicePixelRatio);
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.xr.enabled = true;
appEl.appendChild(renderer.domElement);

scene.add(new THREE.HemisphereLight(0xffffff, 0x444466, 1.0));
const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
keyLight.position.set(1, 2, 1);
scene.add(keyLight);

const reticle = new THREE.Mesh(
  new THREE.RingGeometry(0.07, 0.09, 32).rotateX(-Math.PI / 2),
  new THREE.MeshBasicMaterial({ color: 0x4f46e5, transparent: true, opacity: 0.9 }),
);
reticle.matrixAutoUpdate = false;
reticle.visible = false;
scene.add(reticle);

const previewCube = new THREE.Mesh(
  new THREE.BoxGeometry(0.3, 0.3, 0.3),
  new THREE.MeshStandardMaterial({ color: 0x4f46e5, roughness: 0.3, metalness: 0.2 }),
);
previewCube.position.set(0, 0, -1);
scene.add(previewCube);

window.addEventListener("resize", () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
});

// ── Physics ──────────────────────────────────────────────────────────────
const world = new CANNON.World({ gravity: new CANNON.Vec3(0, -9.82, 0) });
world.broadphase = new CANNON.SAPBroadphase(world);
world.allowSleep = true;

const surfaceMat = new CANNON.Material("surface");
const cubeMat = new CANNON.Material("cube");
const partMat = new CANNON.Material("part");
world.addContactMaterial(
  new CANNON.ContactMaterial(cubeMat, surfaceMat, { friction: 0.4, restitution: 0.25 }),
);
world.addContactMaterial(
  new CANNON.ContactMaterial(cubeMat, cubeMat, { friction: 0.3, restitution: 0.15 }),
);
world.addContactMaterial(
  new CANNON.ContactMaterial(partMat, surfaceMat, { friction: 0.5, restitution: 0.18 }),
);
world.addContactMaterial(
  new CANNON.ContactMaterial(partMat, partMat, { friction: 0.4, restitution: 0.10 }),
);
world.addContactMaterial(
  new CANNON.ContactMaterial(partMat, cubeMat, { friction: 0.4, restitution: 0.18 }),
);

// Implicit floor at y=0 — present from the moment AR starts, before plane
// detection produces a real floor plane (which can take a few seconds).
const implicitFloor = new CANNON.Body({ type: CANNON.Body.STATIC, material: surfaceMat });
implicitFloor.addShape(new CANNON.Plane());
implicitFloor.quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI / 2);
world.addBody(implicitFloor);

type CubeRec = { mesh: THREE.Mesh; body: CANNON.Body };
const cubes: CubeRec[] = [];

type GrabRec = {
  cube: CubeRec;
  controller: THREE.Object3D;
  offset: THREE.Vector3;
  history: Array<{ time: number; pos: THREE.Vector3 }>;
};
const grabs = new Map<THREE.Object3D, GrabRec>();
const GRAB_RANGE = 0.15;
const THROW_HISTORY_MS = 150;
const MAX_THROW_SPEED = 10;

const palette = [0x4f46e5, 0xe11d48, 0x10b981, 0xf59e0b, 0x8b5cf6, 0x06b6d4];

function placeAt(matrix: THREE.Matrix4): void {
  const size = 0.12;
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  matrix.decompose(pos, quat, new THREE.Vector3());
  // Spawn slightly above the surface so gravity drops it cleanly into place.
  pos.y += size;

  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(size, size, size),
    new THREE.MeshStandardMaterial({
      color: palette[cubes.length % palette.length],
      roughness: 0.4,
      metalness: 0.1,
    }),
  );
  mesh.position.copy(pos);
  mesh.quaternion.copy(quat);
  scene.add(mesh);

  const body = new CANNON.Body({
    mass: 0.5,
    material: cubeMat,
    shape: new CANNON.Box(new CANNON.Vec3(size / 2, size / 2, size / 2)),
    position: new CANNON.Vec3(pos.x, pos.y, pos.z),
    quaternion: new CANNON.Quaternion(quat.x, quat.y, quat.z, quat.w),
    angularDamping: 0.2,
    linearDamping: 0.05,
  });
  world.addBody(body);
  cubes.push({ mesh, body });

  setStatus(`${cubes.length} cube${cubes.length === 1 ? "" : "s"} placed.`);
}

function findGrabbable(controller: THREE.Object3D): CubeRec | null {
  let best: CubeRec | null = null;
  let bestDist = GRAB_RANGE;
  for (const c of cubes) {
    if ([...grabs.values()].some((g) => g.cube === c)) continue;
    const d = controller.position.distanceTo(c.mesh.position);
    if (d < bestDist) { best = c; bestDist = d; }
  }
  return best;
}

function tryGrab(controller: THREE.Object3D): boolean {
  const cube = findGrabbable(controller);
  if (!cube) return false;
  // KINEMATIC means physics no longer integrates this body — we drive it.
  // Other dynamic bodies still collide against it using its velocity, so
  // bashing a held cube into a stack actually pushes the stack.
  cube.body.type = CANNON.Body.KINEMATIC;
  cube.body.velocity.setZero();
  cube.body.angularVelocity.setZero();
  cube.body.force.setZero();
  cube.body.torque.setZero();
  // Disable sleep while held: a held-still kinematic body would otherwise
  // doze off and refuse to wake on release.
  cube.body.allowSleep = false;
  cube.body.wakeUp();
  const offset = cube.mesh.position.clone().sub(controller.position);
  grabs.set(controller, {
    cube, controller, offset,
    history: [{ time: performance.now(), pos: controller.position.clone() }],
  });
  return true;
}

function releaseGrab(controller: THREE.Object3D): void {
  const grab = grabs.get(controller);
  if (!grab) return;
  grabs.delete(controller);

  const now = performance.now();
  const recent = grab.history.filter((h) => now - h.time < THROW_HISTORY_MS);
  let vel = new THREE.Vector3();
  if (recent.length >= 2) {
    const a = recent[0];
    const b = recent[recent.length - 1];
    const dt = (b.time - a.time) / 1000;
    if (dt > 0.001) {
      vel = b.pos.clone().sub(a.pos).divideScalar(dt);
      if (vel.length() > MAX_THROW_SPEED) vel.setLength(MAX_THROW_SPEED);
    }
  }
  const body = grab.cube.body;
  body.type = CANNON.Body.DYNAMIC;
  body.force.setZero();
  body.torque.setZero();
  body.angularVelocity.setZero();
  body.velocity.set(vel.x, vel.y, vel.z);
  body.allowSleep = true;
  // Critical: type change alone doesn't wake a sleeping body, and gravity
  // won't apply until it's awake.
  body.wakeUp();
}

function updateGrabs(dt: number): void {
  const now = performance.now();
  for (const grab of grabs.values()) {
    const target = grab.controller.position.clone().add(grab.offset);
    const old = grab.cube.body.position;
    if (dt > 0.0001) {
      grab.cube.body.velocity.set(
        (target.x - old.x) / dt,
        (target.y - old.y) / dt,
        (target.z - old.z) / dt,
      );
    }
    grab.cube.body.position.set(target.x, target.y, target.z);
    grab.history.push({ time: now, pos: grab.controller.position.clone() });
    while (grab.history.length > 1 && now - grab.history[0].time > THROW_HISTORY_MS) {
      grab.history.shift();
    }
  }
}

// ── Plane detection ──────────────────────────────────────────────────────
// body is null for floor planes — they share the implicit infinite floor body
// (we just retune its Y), so we don't create a duplicate collider that would
// sit at slightly the wrong height.
type PlaneRec = { mesh: THREE.Mesh; body: CANNON.Body | null };
const planeRecs = new Map<XRPlane, PlaneRec>();
let implicitFloorY = 0;

const labelColors: Record<string, number> = {
  floor: 0x10b981,
  ceiling: 0x06b6d4,
  wall: 0xf59e0b,
  table: 0x8b5cf6,
  desk: 0x8b5cf6,
  couch: 0xe11d48,
  door: 0x6b7280,
  window: 0x60a5fa,
  screen: 0x60a5fa,
};
const colorForLabel = (label?: string): number =>
  (label && labelColors[label]) || 0x9ca3af;

function syncPlanes(frame: XRFrame, refSpace: XRReferenceSpace): void {
  const detected = frame.detectedPlanes;
  if (!detected) return;

  for (const [plane, rec] of planeRecs) {
    if (!detected.has(plane)) {
      if (rec.body) world.removeBody(rec.body);
      scene.remove(rec.mesh);
      rec.mesh.geometry.dispose();
      (rec.mesh.material as THREE.Material).dispose();
      planeRecs.delete(plane);
    }
  }

  let lowestFloorY: number | null = null;

  for (const plane of detected) {
    const pose = frame.getPose(plane.planeSpace, refSpace);
    if (!pose) continue;

    const isFloor =
      plane.orientation === "horizontal" && plane.semanticLabel === "floor";

    let rec = planeRecs.get(plane);
    if (!rec) {
      const polygon = plane.polygon;
      // XRPlane polygons live in the plane's own space (X/Z, Y=0). Build a 2D
      // shape from those points and rotate it flat in three.js.
      const shape2D = new THREE.Shape(
        polygon.map((pt) => new THREE.Vector2(pt.x, pt.z)),
      );
      const geom = new THREE.ShapeGeometry(shape2D);
      geom.rotateX(-Math.PI / 2);
      const mesh = new THREE.Mesh(
        geom,
        new THREE.MeshBasicMaterial({
          color: colorForLabel(plane.semanticLabel),
          transparent: true,
          opacity: 0.18,
          side: THREE.DoubleSide,
          depthWrite: false,
        }),
      );
      mesh.matrixAutoUpdate = false;
      scene.add(mesh);

      let body: CANNON.Body | null = null;
      if (!isFloor) {
        const xs = polygon.map((pt) => pt.x);
        const zs = polygon.map((pt) => pt.z);
        const w = Math.max(0.1, Math.max(...xs) - Math.min(...xs));
        const d = Math.max(0.1, Math.max(...zs) - Math.min(...zs));
        body = new CANNON.Body({ type: CANNON.Body.STATIC, material: surfaceMat });
        body.addShape(new CANNON.Box(new CANNON.Vec3(w / 2, 0.005, d / 2)));
        world.addBody(body);
      }

      rec = { mesh, body };
      planeRecs.set(plane, rec);
    }

    rec.mesh.matrix.fromArray(pose.transform.matrix);
    const tmpPos = new THREE.Vector3();
    const tmpQuat = new THREE.Quaternion();
    const tmpScale = new THREE.Vector3();
    rec.mesh.matrix.decompose(tmpPos, tmpQuat, tmpScale);
    if (rec.body) {
      rec.body.position.set(tmpPos.x, tmpPos.y, tmpPos.z);
      rec.body.quaternion.set(tmpQuat.x, tmpQuat.y, tmpQuat.z, tmpQuat.w);
    }

    if (isFloor) {
      if (lowestFloorY === null || tmpPos.y < lowestFloorY) lowestFloorY = tmpPos.y;
    }
  }

  // Snap the implicit infinite floor to the detected floor's Y so we don't
  // have two near-coincident floors fighting each other.
  if (lowestFloorY !== null && Math.abs(lowestFloorY - implicitFloorY) > 0.002) {
    implicitFloorY = lowestFloorY;
    implicitFloor.position.set(0, lowestFloorY, 0);
  }
}

// ── Mesh detection (occluders + accurate physics colliders) ─────────────
// Quest 3's room scan exposes triangle meshes via `mesh-detection`. We use
// each mesh as both a depth-only occluder (so virtual things hide behind real
// geometry) and a static `CANNON.Trimesh` collider (for accurate physics).
type MeshRec = {
  occluder: THREE.Mesh;
  body: CANNON.Body | null;
};
const meshRecs = new Map<XRMesh, MeshRec>();

function syncMeshes(frame: XRFrame, refSpace: XRReferenceSpace): void {
  const detected = frame.detectedMeshes;
  if (!detected) return;

  // Cleanup vanished meshes
  for (const [m, rec] of meshRecs) {
    if (!detected.has(m)) {
      if (rec.body) world.removeBody(rec.body);
      scene.remove(rec.occluder);
      rec.occluder.geometry.dispose();
      (rec.occluder.material as THREE.Material).dispose();
      meshRecs.delete(m);
    }
  }

  for (const m of detected) {
    const pose = frame.getPose(m.meshSpace, refSpace);
    if (!pose) continue;

    let rec = meshRecs.get(m);
    if (!rec) {
      // Build BufferGeometry from the mesh's vertices/indices. WebXR gives
      // both as typed arrays already in the right format.
      const geom = new THREE.BufferGeometry();
      geom.setAttribute("position", new THREE.BufferAttribute(m.vertices, 3));
      geom.setIndex(new THREE.BufferAttribute(m.indices, 1));
      geom.computeVertexNormals();

      // Depth-only occluder: writes to the depth buffer but not color, so
      // virtual fragments behind it fail the depth test and don't draw.
      const mat = new THREE.MeshBasicMaterial({
        colorWrite: false,
        depthWrite: true,
      });
      const occluder = new THREE.Mesh(geom, mat);
      occluder.matrixAutoUpdate = false;
      // Render before everything else so its depth values are in the buffer
      // by the time the rest of the scene is drawn.
      occluder.renderOrder = -1;
      scene.add(occluder);

      // Trimesh collider — exact triangles, replaces the plane bbox box for
      // this geometry. Floor is excluded because the implicit infinite floor
      // already handles it (see syncPlanes), and a trimesh of the floor would
      // be wasteful.
      let body: CANNON.Body | null = null;
      const isFloor = m.semanticLabel === "floor";
      if (!isFloor) {
        try {
          // Cannon-es Trimesh wants regular number arrays, not typed arrays.
          const verts = Array.from(m.vertices);
          const idxs = Array.from(m.indices);
          const tri = new CANNON.Trimesh(verts, idxs);
          body = new CANNON.Body({ type: CANNON.Body.STATIC, material: surfaceMat });
          body.addShape(tri);
          world.addBody(body);
        } catch (err) {
          console.warn("Failed to build Trimesh for mesh", err);
        }
      }

      rec = { occluder, body };
      meshRecs.set(m, rec);
    }

    rec.occluder.matrix.fromArray(pose.transform.matrix);
    if (rec.body) {
      const tmpPos = new THREE.Vector3();
      const tmpQuat = new THREE.Quaternion();
      const tmpScale = new THREE.Vector3();
      rec.occluder.matrix.decompose(tmpPos, tmpQuat, tmpScale);
      rec.body.position.set(tmpPos.x, tmpPos.y, tmpPos.z);
      rec.body.quaternion.set(tmpQuat.x, tmpQuat.y, tmpQuat.z, tmpQuat.w);
    }
  }
}

// ── Hands ────────────────────────────────────────────────────────────────
const handFactory = new XRHandModelFactory();
for (let i = 0; i < 2; i++) {
  const hand = renderer.xr.getHand(i);
  hand.add(handFactory.createHandModel(hand, "mesh"));
  scene.add(hand);
}

// ── WebXR session ────────────────────────────────────────────────────────
type XRSystem = {
  isSessionSupported(mode: "immersive-ar" | "immersive-vr"): Promise<boolean>;
  requestSession(
    mode: "immersive-ar" | "immersive-vr",
    init?: XRSessionInit,
  ): Promise<XRSession>;
};

const xr: XRSystem | undefined = (navigator as Navigator & { xr?: XRSystem }).xr;

let hitTestSource: XRHitTestSource | null = null;
let currentSession: XRSession | null = null;

// ── Controller beam (pointer line + tip dot) ────────────────────────────
const BEAM_DEFAULT_LEN = 1.5;

function makeControllerBeam(): THREE.Group {
  const group = new THREE.Group();
  const lineGeom = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 0, -1),
  ]);
  const line = new THREE.Line(
    lineGeom,
    new THREE.LineBasicMaterial({
      color: 0xffffff, transparent: true, opacity: 0.55, depthTest: false,
    }),
  );
  line.scale.z = BEAM_DEFAULT_LEN;
  line.renderOrder = 999;
  group.add(line);

  const dot = new THREE.Mesh(
    new THREE.SphereGeometry(0.008, 12, 8),
    new THREE.MeshBasicMaterial({ color: 0x4f46e5, depthTest: false }),
  );
  dot.position.set(0, 0, -BEAM_DEFAULT_LEN);
  dot.renderOrder = 999;
  group.add(dot);

  group.userData.line = line;
  group.userData.dot = dot;
  return group;
}

function setBeamLength(controller: THREE.Object3D, distance: number): void {
  const beam = controller.userData.beam as THREE.Group | undefined;
  if (!beam) return;
  const line = beam.userData.line as THREE.Line;
  const dot = beam.userData.dot as THREE.Mesh;
  line.scale.z = distance;
  dot.position.set(0, 0, -distance);
}

// ── Tool dispatch ────────────────────────────────────────────────────────
type Tool = "panel" | "cubes" | "parts";
let currentTool: Tool = "panel";
let toolPanel: ToolPanel | null = null;
let partsTool: PartsToolHandle | null = null;
const controllers: THREE.Object3D[] = [];

let pendingPanelPlacement = false;

function showPanel(): void {
  if (toolPanel) {
    scene.remove(toolPanel.group);
    toolPanel = null;
  }
  toolPanel = new ToolPanel("Choose a tool", [
    { label: "AR Cubes", onSelect: () => pickTool("cubes") },
    { label: "Part Viewer", onSelect: () => pickTool("parts") },
  ]);
  // Hide until we have a valid headset pose. On session start, the Three.js
  // camera hasn't been synced to the headset yet — its world position is the
  // origin, which under local-floor reference is the user's floor.
  toolPanel.group.visible = false;
  scene.add(toolPanel.group);
  currentTool = "panel";
  pendingPanelPlacement = true;
  setStatus("Pinch a button to choose a tool.");
}

function pickTool(tool: Exclude<Tool, "panel">): void {
  if (toolPanel) {
    scene.remove(toolPanel.group);
    toolPanel = null;
  }
  if (partsTool) {
    partsTool.dispose();
    partsTool = null;
  }
  currentTool = tool;
  if (tool === "cubes") {
    setStatus("AR Cubes — pinch a surface to drop. Press B/Y to return to panel.");
  } else if (tool === "parts") {
    if (!currentSession) {
      setStatus("No active session.");
      return;
    }
    setStatus("Loading Part Viewer…");
    void startPartsTool(scene, camera, currentSession, world, partMat, setStatus).then((handle) => {
      partsTool = handle;
    });
  }
}

function clearCubes(): void {
  for (const c of cubes) {
    world.removeBody(c.body);
    scene.remove(c.mesh);
  }
  cubes.length = 0;
  grabs.clear();
}

function returnToPanel(): void {
  if (currentTool === "panel") return;
  // Let the active tool consume the back press if it has an overlay to
  // dismiss first (e.g. parts list when parts are already placed).
  if (currentTool === "parts" && partsTool && partsTool.tryDismissOverlay()) {
    return;
  }
  if (partsTool) { partsTool.dispose(); partsTool = null; }
  if (currentTool === "cubes") clearCubes();
  showPanel();
}

// Tracks previous press state per input source for edge detection on the
// "back" button (B on the right controller, Y on the left). Reset on session
// end. Hands have no gamepad buttons — back-via-controllers only.
const prevBackBtn = new Map<XRInputSource, boolean>();

function pollBackButton(): void {
  if (!currentSession || currentTool === "panel") return;
  let pressedNow = false;
  for (const src of currentSession.inputSources) {
    const gp = src.gamepad;
    if (!gp || gp.buttons.length < 6) continue;
    const isDown = gp.buttons[5]?.pressed ?? false;
    const wasDown = prevBackBtn.get(src) ?? false;
    if (isDown && !wasDown) pressedNow = true;
    prevBackBtn.set(src, isDown);
  }
  // GC stale entries
  if (prevBackBtn.size > 4) {
    const live = new Set(currentSession.inputSources);
    for (const k of [...prevBackBtn.keys()]) if (!live.has(k)) prevBackBtn.delete(k);
  }
  if (pressedNow) returnToPanel();
}

async function checkSupport(): Promise<void> {
  if (!xr) {
    setStatus("WebXR not available — open this page on the Quest 3 in the Meta Browser.");
    enterBtn.disabled = true;
    return;
  }
  try {
    const ok = await xr.isSessionSupported("immersive-ar");
    if (ok) {
      setStatus("Ready. Put on your headset and tap “Enter AR”.");
      enterBtn.disabled = false;
    } else {
      setStatus("immersive-ar not supported on this device.");
      enterBtn.disabled = true;
    }
  } catch (err) {
    setStatus(`WebXR check failed: ${(err as Error).message}`);
    enterBtn.disabled = true;
  }
}

async function enterAR(): Promise<void> {
  if (!xr) return;
  enterBtn.disabled = true;
  setStatus("Starting AR session…");

  let session: XRSession;
  try {
    session = await xr.requestSession("immersive-ar", {
      requiredFeatures: ["hit-test", "local-floor"],
      optionalFeatures: [
        "dom-overlay",
        "anchors",
        "plane-detection",
        "mesh-detection",
        "hand-tracking",
      ],
      domOverlay: { root: overlayEl },
    } as XRSessionInit & { domOverlay?: { root: Element } });
  } catch (err) {
    setStatus(`Could not start AR: ${(err as Error).message}`);
    enterBtn.disabled = false;
    return;
  }

  previewCube.visible = false;
  enterBtn.style.display = "none";

  await renderer.xr.setSession(session as unknown as XRSession);
  currentSession = session;

  const viewerSpace = await session.requestReferenceSpace("viewer");
  const refSpace = await session.requestReferenceSpace("local-floor");
  renderer.xr.setReferenceSpace(refSpace);

  if ("requestHitTestSource" in session) {
    hitTestSource = (await (session as XRSession & {
      requestHitTestSource: (opts: { space: XRReferenceSpace }) => Promise<XRHitTestSource>;
    }).requestHitTestSource({ space: viewerSpace })) ?? null;
  }

  const onStart = (ctrl: THREE.Object3D) => () => {
    if (currentTool === "panel" && toolPanel) {
      const btn = toolPanel.updateHover(ctrl);
      if (btn) btn.onSelect();
      return;
    }
    if (currentTool === "cubes") {
      if (tryGrab(ctrl)) return;
      if (reticle.visible) placeAt(reticle.matrix);
      return;
    }
    if (currentTool === "parts" && partsTool) {
      partsTool.onSelectStart(ctrl);
    }
  };
  const onEnd = (ctrl: THREE.Object3D) => () => {
    if (currentTool === "cubes") {
      releaseGrab(ctrl);
      return;
    }
    if (currentTool === "parts" && partsTool) {
      partsTool.onSelectEnd(ctrl);
    }
  };
  controllers.length = 0;
  for (const i of [0, 1]) {
    const ctrl = renderer.xr.getController(i);
    ctrl.addEventListener("selectstart", onStart(ctrl));
    ctrl.addEventListener("selectend", onEnd(ctrl));
    const beam = makeControllerBeam();
    ctrl.add(beam);
    ctrl.userData.beam = beam;
    scene.add(ctrl);
    controllers.push(ctrl);
  }

  document.body.classList.add("in-xr");
  showPanel();

  session.addEventListener("end", () => {
    hitTestSource = null;
    grabs.clear();
    for (const rec of planeRecs.values()) {
      if (rec.body) world.removeBody(rec.body);
      scene.remove(rec.mesh);
    }
    implicitFloorY = 0;
    implicitFloor.position.set(0, 0, 0);
    planeRecs.clear();
    for (const rec of meshRecs.values()) {
      if (rec.body) world.removeBody(rec.body);
      scene.remove(rec.occluder);
      rec.occluder.geometry.dispose();
      (rec.occluder.material as THREE.Material).dispose();
    }
    meshRecs.clear();
    for (const c of cubes) {
      world.removeBody(c.body);
      scene.remove(c.mesh);
    }
    cubes.length = 0;

    if (toolPanel) { scene.remove(toolPanel.group); toolPanel = null; }
    if (partsTool) { partsTool.dispose(); partsTool = null; }
    currentTool = "panel";
    currentSession = null;
    prevBackBtn.clear();
    pendingPanelPlacement = false;

    document.body.classList.remove("in-xr");
    previewCube.visible = true;
    enterBtn.style.display = "";
    enterBtn.disabled = false;
    setStatus("AR session ended. Tap “Enter AR” to start again.");
  });
}

enterBtn.addEventListener("click", () => {
  void enterAR();
});

// ── Render loop ──────────────────────────────────────────────────────────
const clock = new THREE.Clock();

renderer.setAnimationLoop((_time, frame) => {
  const dt = clock.getDelta();
  const refSpace = renderer.xr.getReferenceSpace();

  if (frame && refSpace) {
    if (pendingPanelPlacement && toolPanel) {
      const viewerPose = frame.getViewerPose(refSpace);
      if (viewerPose) {
        const t = viewerPose.transform;
        const headPos = new THREE.Vector3(t.position.x, t.position.y, t.position.z);
        const headQuat = new THREE.Quaternion(
          t.orientation.x, t.orientation.y, t.orientation.z, t.orientation.w,
        );
        toolPanel.placeInFrontOfPose(headPos, headQuat, 1.0);
        toolPanel.group.visible = true;
        pendingPanelPlacement = false;
      }
    }

    if (hitTestSource && currentTool === "cubes") {
      const hits = frame.getHitTestResults(hitTestSource);
      if (hits.length > 0) {
        const pose = hits[0].getPose(refSpace);
        if (pose) {
          reticle.visible = true;
          reticle.matrix.fromArray(pose.transform.matrix);
        }
      } else {
        reticle.visible = false;
      }
    } else {
      reticle.visible = false;
    }
    syncPlanes(frame, refSpace);
    syncMeshes(frame, refSpace);
  }

  if (currentTool === "panel" && toolPanel) {
    for (const ctrl of controllers) {
      toolPanel.updateHover(ctrl);
      const d = toolPanel.hoverDistance(ctrl);
      setBeamLength(ctrl, d ?? BEAM_DEFAULT_LEN);
    }
  } else if (currentTool === "parts" && partsTool) {
    partsTool.update(dt, controllers, frame ?? null, refSpace ?? null);
    for (const ctrl of controllers) {
      const d = partsTool.listHoverDistance(ctrl);
      setBeamLength(ctrl, d ?? BEAM_DEFAULT_LEN);
    }
  } else {
    for (const ctrl of controllers) setBeamLength(ctrl, BEAM_DEFAULT_LEN);
  }
  pollBackButton();

  if (renderer.xr.isPresenting) {
    updateGrabs(dt);
    world.step(1 / 60, dt, 3);
    for (const c of cubes) {
      c.mesh.position.set(c.body.position.x, c.body.position.y, c.body.position.z);
      c.mesh.quaternion.set(
        c.body.quaternion.x,
        c.body.quaternion.y,
        c.body.quaternion.z,
        c.body.quaternion.w,
      );
    }
  } else {
    previewCube.rotation.x += 0.005;
    previewCube.rotation.y += 0.008;
  }

  renderer.render(scene, camera);
});

void checkSupport();
